/**************
 Cart Logic & Optimization
 ***************/

const total = 8000; // Free shipping threshold
let isCartUpdating = false;

// Global Hidden SKUs
const hiddenSKUs = [9693835952442, 8724402569530, 9804475040058, 9804474777914, 9878383395130, 10028451823930];

/**
 * MAIN FUNCTION: OPEN CART
 * Optimized for speed: Renders first, checks gifts second.
 */
async function openCart(skipFetch = false) {
    if (isCartUpdating) return;
    isCartUpdating = true;

    try {
        // 1. FETCH: Get the cart state
        if (skipFetch === false || typeof cart === 'undefined') {
            cart = await fetch("/cart.js").then(data => data.json());
        }

        // 2. FAST RENDER: Show the cart immediately so the user doesn't wait
        renderCartDOM(cart);
        document.querySelector("#miniCart").showModal();
        document.querySelector("body").classList.add("noScroll");

        // 3. BACKGROUND WORK: Check gifts silently
        // This runs while the user is already looking at the cart
        const updatedCart = await handleFreeGiftsCheck(cart);
        
        // 4. UPDATE IF NEEDED: Only re-render if gifts caused a change
        if (updatedCart) {
            cart = updatedCart;
            renderCartDOM(cart); // Update view with new gifts
        }

        addUpsellItems();

    } catch (err) {
        console.error("Cart Error:", err);
    } finally {
        isCartUpdating = false;
        attachCartListeners();
    }
}

/**
 * RENDER FUNCTION
 * Handles all HTML generation. Separated for performance.
 */
function renderCartDOM(currentCart) {
    const cartBody = document.querySelector("#miniCartBody");
    cartBody.innerHTML = "";

    const effectiveCount = getEffectiveCartCount(currentCart);

    // Update Header
    document.querySelectorAll(".headerCart").forEach(el => {
        el.setAttribute("count", effectiveCount);
    });

    // Handle Empty vs Active State
    if (effectiveCount === 0) {
        setCartEmptyState();
    } else {
        setCartActiveState(currentCart);
        
        // Loop Items
        currentCart.items.forEach(item => {
            if (hiddenSKUs.includes(item.product_id)) return; // Hide SKUs

            // Generate HTML for this item
            const itemHTML = buildCartItemHTML(item);
            cartBody.insertAdjacentHTML("beforeend", itemHTML);
            
            // Render Subscription Options (if any)
            renderSubscriptionOptions(item, cartBody);
        });
    }
}

/**
 * HELPER: Build HTML for a single item
 */
function buildCartItemHTML(item) {
    let sellingPlans = productSellingPlans[item.product_id] || [];
    
    // Sub Button HTML
    let subHtml = "";
    if (sellingPlans.length > 0) {
        subHtml = `
        <button class="miniCartBodyItemSubscribe ${item.selling_plan_allocation?.selling_plan?.name ? 'active' : ''}">
        ${item.selling_plan_allocation?.selling_plan?.name ? 'You’re Saving 15% by Subscribing' : 'Subscribe and Save 15%'}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M13.5 7C13.5 10.5899 10.5899 13.5 7 13.5C3.41015 13.5 0.5 10.5899 0.5 7C0.5 3.41015 3.41015 0.5 7 0.5C10.5899 0.5 13.5 3.41015 13.5 7ZM7 8.02138L4.6218 10.3996L3.60042 9.3782L5.97862 7L3.60042 4.6218L4.6218 3.60042L7 5.97862L9.3782 3.60042L10.3996 4.6218L8.02138 7L10.3996 9.3782L9.3782 10.3996L7 8.02138Z" fill="#0C3E2B"/>
            </svg>
        </button>
        <div class="miniCartBodyItemSubscribeSelect" name="sellingPlan-" aria-label="subscription select"></div>`;
    }

    // Sub Title HTML
    let subTitle = "";
    if (item.selling_plan_allocation?.selling_plan?.name) {
        subTitle = `<div class="miniCartBodyItemSellingPlan"><span>Delivery every:</span> ${item.selling_plan_allocation.selling_plan.name?.replace("Every", "")}</div>`;
    }

    return `
    <div class="miniCartBodyItem" key="${item.key}">
        <div class="miniCartBodyItemTop">
            <img class="miniCartBodyItemImage" src="${item.image}&width=200">
            <div>
                <div class="miniCartBodyItemTitle">${item.product_title}</div>
                <div class="miniCartBodyItemPrice">${price(item.final_line_price / item.quantity)} 
                <s>${item.selling_plan_allocation?.selling_plan?.name
                    ? price(item.selling_plan_allocation.compare_at_price)
                    : item.original_line_price > item.final_line_price
                        ? price(item.original_line_price / item.quantity)
                        : ""}</s></div>
                <div class="miniCartBodyItemQuantity">
                    <button class="miniCartBodyItemQuantityMinus"></button>
                    <div class="miniCartBodyItemQuantityCount">${item.quantity}</div>
                    <button class="miniCartBodyItemQuantityPlus"></button>
                </div>
            </div>
            <svg class="miniCartBodyItemDelete" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="#ffffff" d="M3 6v18h18v-18h-18zm5 14c0 .552-.448 1-1 1s-1-.448-1-1v-10c0-.552.448-1 1-1s1 .448 1 1v10zm5 0c0 .552-.448 1-1 1s-1-.448-1-1v-10c0-.552.448-1 1-1s1 .448 1 1v10zm5 0c0 .552-.448 1-1 1s-1-.448-1-1v-10c0-.552.448-1 1-1s1 .448 1 1v10zm4-18v2h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.315c0 .901.73 2 1.631 2h5.712z"/></svg>
        </div>
        <div class="miniCartBodyItemBottom">
            ${subHtml}
            ${subTitle}
        </div>
    </div>`;
}

function renderSubscriptionOptions(item, container) {
    let sellingPlans = productSellingPlans[item.product_id] || [];
    if (sellingPlans.length > 0) {
        let plansToSkip = [691293258042, 691293290810, 691293323578];
        const selectContainer = container.querySelector(`.miniCartBodyItem[key="${item.key}"] .miniCartBodyItemSubscribeSelect`);
        if(!selectContainer) return;

        sellingPlans.forEach((plan) => {
            if (plansToSkip.includes(plan.id)) return;
            let option = document.createElement("label");
            let input = document.createElement("input");
            if (plan.name === item.selling_plan_allocation?.selling_plan?.name) option.classList.add("active");
            input.setAttribute("value", plan.id);
            input.setAttribute("type", "radio");
            input.setAttribute("name", `subscription-${item.key}`);
            option.textContent = plan.name?.replace("Every", "");
            option.appendChild(input);
            selectContainer.appendChild(option);
        });
        if (selectContainer.querySelectorAll('label').length <= 1) selectContainer.style.display = 'none';
    }
}

/**
 * UTILITIES & STATES
 */
function getEffectiveCartCount(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((count, item) => {
        if (typeof freeGifts !== 'undefined' && freeGifts.includes(item.variant_id)) return count;
        if (hiddenSKUs.includes(item.product_id)) return count;
        return count + item.quantity;
    }, 0);
}

function setCartActiveState(cart) {
    document.querySelector("#miniCartProgressSection").classList.add("active");
    document.querySelector("#miniCartHeader").classList.add("active");
    document.querySelector("#miniCartBody").classList.add("active");
    document.querySelector("#miniCartEmpty").classList.remove("active");
    document.querySelector("#miniCartFooter").classList.add("active");
    document.querySelector('#miniCartFreeGifts').classList.add('active');

    updateProgressBar(cart.items_subtotal_price, total);
    
    const subtotalEl = document.querySelector("#miniCartFooterSubtotal");
    if(subtotalEl) {
        const spans = subtotalEl.querySelectorAll('span');
        if(spans.length > 2) {
             spans[spans.length - 2].textContent = price(cart.items_subtotal_price);
             spans[spans.length - 1].textContent = price(cart.items_subtotal_price);
        }
    }
}

function setCartEmptyState() {
    document.querySelector("#miniCartProgressSection").classList.remove("active");
    document.querySelector("#miniCartHeader").classList.remove("active");
    document.querySelector("#miniCartBody").classList.remove("active");
    document.querySelector("#miniCartEmpty").classList.add("active");
    document.querySelector("#miniCartFooter").classList.remove("active");
    document.querySelector('#miniCartFreeGifts').classList.remove('active');
    document.querySelectorAll(".miniCartEnticementContainer")?.forEach((el) => el.classList.remove("active"));
}

// Logic to check/add/remove gifts safely
async function handleFreeGiftsCheck(currentCart) {
    if (typeof freeGifts === 'undefined' || freeGifts.length === 0) return null;

    let cartHasSubscription = currentCart.items.some(item => item.selling_plan_allocation?.selling_plan?.name);
    let updates = {};
    let itemsToAdd = [];
    let needsUpdate = false;

    const giftsInCart = currentCart.items.filter(item => freeGifts.includes(item.variant_id));
    const giftIdsInCart = giftsInCart.map(item => item.variant_id);

    if (cartHasSubscription) {
        for (const giftId of freeGifts) {
            if (!giftIdsInCart.includes(giftId)) {
                itemsToAdd.push({
                    id: giftId,
                    quantity: (giftId === 50618185777466) ? 2 : 1
                });
                needsUpdate = true;
            }
        }
    } else {
        if (giftsInCart.length > 0) {
            giftsInCart.forEach(item => { updates[item.key] = 0; });
            needsUpdate = true;
        }
    }

    if (needsUpdate) {
        // Execute updates silently
        if (Object.keys(updates).length > 0) {
             await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
            });
        }
        if (itemsToAdd.length > 0) {
             await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: itemsToAdd })
            });
        }
        // Fetch new state
        const freshCart = await fetch("/cart.js").then(res => res.json());
        document.querySelector('#miniCartFreeGiftsActive').classList.toggle('active', cartHasSubscription);
        return freshCart; 
    } else {
        document.querySelector('#miniCartFreeGiftsActive').classList.toggle('active', cartHasSubscription);
        return null;
    }
}

// Global Event Listeners & Updates
function attachCartListeners() {
    document.querySelectorAll(`.miniCartBodyItem`).forEach(function (element) {
        let key = element.getAttribute("key");
        let quantity = parseInt(element.querySelector(".miniCartBodyItemQuantityCount")?.textContent || 0);

        element.querySelector(".miniCartBodyItemQuantityMinus").addEventListener("click", () => updateCartItemQty(key, quantity - 1, element));
        element.querySelector(".miniCartBodyItemQuantityPlus").addEventListener("click", () => updateCartItemQty(key, quantity + 1, element));
        element.querySelector(".miniCartBodyItemDelete").addEventListener("click", () => updateCartItemQty(key, 0, element));
        
        element.querySelector(".miniCartBodyItemSubscribe")?.addEventListener("click", function () {
            if (this.classList.contains("active")) changeItemProps(key, quantity, null);
            else this.nextElementSibling.querySelector('input')?.click();
        });

        element.querySelector(".miniCartBodyItemSubscribeSelect")?.addEventListener("change", function (event) {
             changeItemProps(key, quantity, event.target.value);
        });
    });
}

async function updateCartItemQty(key, quantity, element) {
    if (isCartUpdating) return;
    if (quantity === 0) element.classList.add("fade");
    else element.querySelector(".miniCartBodyItemQuantityCount").textContent = quantity;

    isCartUpdating = true;
    try {
        let updates = {};
        updates[key] = quantity;
        await fetch('/cart/update.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({ updates: updates })
        });
    } catch(e) { console.error(e); } 
    finally {
        isCartUpdating = false;
        await openCart();
    }
}

async function changeItemProps(key, quantity, sellingPlanId) {
    if (isCartUpdating) return;
    isCartUpdating = true;
    try {
        await fetch('/cart/change.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({ id: key, quantity: quantity, selling_plan: sellingPlanId })
        });
    } catch(e) { console.error(e); } 
    finally {
        isCartUpdating = false;
        await openCart();
    }
}

const progressBar = document.getElementById("miniCartProgressBar");
const firstText = document.getElementById("miniCartProgressText");
function updateProgressBar(subtotal, total) {
    if(!progressBar || !firstText) return;
    const progressPercentage = Math.min((subtotal / total) * 100, 100);
    const difference = total / 100 - subtotal / 100;

    if (progressBar.style.width !== `${progressPercentage}%`) progressBar.style.width = `${progressPercentage}%`;

    const isCloseToTotal = subtotal < total;
    const isAtTotal = subtotal >= total;

    firstText.innerHTML = isAtTotal
        ? `<span>You've unlocked <b>FREE Shipping!</b> (US Only)</span>`
        : `<span>Spend <b>$${difference.toFixed(2)}</b> to achieve <b>FREE Shipping</b></span>`;

    progressBar.classList.toggle("full-glow", isAtTotal);
    document.querySelector('.miniCartProgressBarEndOne')?.classList.toggle("active", !isAtTotal);
    document.querySelector('.miniCartProgressBarEndTwo')?.classList.toggle("active", isAtTotal);
    progressBar.classList.toggle("half-glow", isCloseToTotal && !isAtTotal);
}

function closeCart() {
    document.querySelector("#miniCart").close();
    document.querySelector("body").classList.remove("noScroll");
}

document.querySelectorAll(".openCart").forEach(el => el.addEventListener("click", () => openCart()));
document.querySelectorAll(".closeCart").forEach(el => el.addEventListener("click", closeCart));
document.querySelector("#miniCart").addEventListener("click", function (e) { if (e.target === this) closeCart(); });
document.querySelector("#miniCartFooterCheckout").addEventListener("click", function () {
    this.classList.add("loading");
    window.location.href = "/checkout";
});
if (typeof dialogPolyfill !== 'undefined') dialogPolyfill.registerDialog(document.querySelector("#miniCart"));

function addUpsellItems() {
    if (!cart || cart.item_count === 0) return;
    let cartItems = [];
    let bundleInCart = false;
    if(cart.items) {
        for (let item of cart.items) {
            if (item.id === 48190164533562) { bundleInCart = true; break; }
            cartItems.push(item.product_id);
        }
    }

    if (bundleInCart) {
        if (cartItems.includes(8230270468410)) return;
        document.querySelector(`.miniCartEnticementContainer[data-id="${8230270468410}"]`)?.classList.add("active");
    } else {
        document.querySelectorAll(".miniCartEnticementContainer").forEach((el) => {
            if (upsellIds.includes(parseInt(el.dataset.id)) && !cartItems.includes(parseInt(el.dataset.id))) {
                el.classList.add("active");
            } else {
                el.classList.remove("active");
            }
        });
    }
}

document.querySelectorAll(".miniCartEnticementBottom").forEach((el) => {
    el.addEventListener("click", async function (e) {
        if (e.target.tagName !== "BUTTON") return;
        let sellingPlan = e.target.classList.contains("miniCartEnticementButtonSubscribe");
        let upsellId = this.dataset.id;
        let subscription = this.dataset.subscribe;
        e.target.classList.add("loading");

        await fetch('/cart/add.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({ items: [{ quantity: 1, id: upsellId, selling_plan: sellingPlan ? subscription : null }] })
        });

        e.target.classList.remove("loading");
        document.querySelectorAll(".miniCartEnticementContainer").forEach((el) => el.classList.remove("active"));
        await openCart();
    });
});

document.addEventListener('DOMContentLoaded', async () => {
    try {
        const initialCart = await fetch("/cart.js").then(res => res.json());
        const count = getEffectiveCartCount(initialCart);
        document.querySelectorAll(".headerCart").forEach(el => el.setAttribute("count", count));
    } catch (e) { console.error("Header Count Init Error", e); }
});

function price(cents) {
    return '$' + (cents / 100).toFixed(2);
}